import React, { ComponentType, ReactNode, useEffect, useMemo, useState } from "react"
import { FlyingCarpetPage, useFlyingCarpet } from "./flying-carpet"
import { Dialog } from "radix-ui"
import { CLOSE_ICON } from "@features/atoms/svg"
import { CartCarpetPage } from "./Carpet.Cart"
import {
  AccountCarpetPage,
  ChangePasswordCarpetPage,
  CreateAccountCarpetPage,
  DetailsCarpetPage,
  ForgotPasswordCarpetPage,
  LoginCarpetPage,
  ResetPasswordCarpetPage,
} from "./Carpet.Account"
import { TicketsCarpetPage } from "./Carpet.Tickets"
import { accountStore } from "@features/account/account-store"
import { useAccount } from "@features/account/useAccount"
import { useCheckoutConfig } from "@features/tickets/useCheckoutConfig"
import { Spinner } from "@features/motion/Spinner"
import { useRouterEvents, useRouteTransition } from "eddev/routing"
import { animate, AnimatePresence, motion, useMotionValue } from "motion/react"
import { useIsMobile } from "@hooks/useIsMobile"
import { tv } from "@utils/tw"

export function FlyingCarpet() {
  const carpet = useFlyingCarpet()
  const isMobile = useIsMobile(800)

  const styles = carpetStyles()

  return (
    <Dialog.Root
      open={carpet.isOpen}
      onOpenChange={(open) => {
        if (!open) carpet.close()
      }}
    >
      <Dialog.Portal forceMount>
        <AnimatePresence>
          {carpet.isOpen && (
            <Dialog.Overlay data-modal-overlay asChild className={styles.overlay()}>
              <motion.div />
            </Dialog.Overlay>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {carpet.isOpen && (
            <motion.div
              initial={isMobile ? { y: "100vh", opacity: 1 } : { opacity: 0, scale: 0.95 }}
              animate={isMobile ? { y: "0", opacity: 1 } : { opacity: 1, scale: 1 }}
              exit={isMobile ? { y: "100vh", opacity: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
              className={styles.contentWrapper()}
            >
              <Dialog.Content className={styles.content()}>
                <FlyingCarpetContents />
              </Dialog.Content>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const carpetStyles = tv({
  slots: {
    overlay: "fixed inset-0 bg-blue/50 z-40 data-[state=open]:animate-overlayShow",
    contentWrapper:
      "fixed top-headerHeight z-50 bottom-0 md:bottom-grid-margin left-0 md:left-auto right-0 md:right-grid-margin md:w-[400px] bg-white border border-blue !outline-none origin-top-right",
    content: "h-full outline-none",
  },
})

type CarpetAccess = "customer" | "guest"

const CARPET_PAGES: Record<
  FlyingCarpetPage,
  {
    label?: string
    backTarget?: FlyingCarpetPage
    /** Limits a page to signed in customers or to guests, redirecting everyone else. */
    access?: CarpetAccess
    component?: ComponentType<{}>
  }
> = {
  account: {
    label: "Account",
    access: "customer",
    component: AccountCarpetPage,
  },
  "account/tickets": {
    label: "Account",
    backTarget: "account",
    access: "customer",
    component: TicketsCarpetPage,
  },
  "account/details": {
    label: "Account",
    backTarget: "account",
    access: "customer",
    component: DetailsCarpetPage,
  },
  "account/change-password": {
    label: "Account",
    backTarget: "account",
    access: "customer",
    component: ChangePasswordCarpetPage,
  },
  "account/login": {
    label: "Account",
    access: "guest",
    component: LoginCarpetPage,
  },
  "account/create": {
    label: "Account",
    access: "guest",
    component: CreateAccountCarpetPage,
  },
  "account/forgot-password": {
    label: "Account",
    access: "guest",
    component: ForgotPasswordCarpetPage,
  },
  // Open to everyone, since a signed in customer can follow a reset link too.
  "account/reset-password": {
    label: "Account",
    component: ResetPasswordCarpetPage,
  },
  cart: {
    label: "Cart",
    component: CartCarpetPage,
  },
  none: {},
}

function accessRedirect(access: CarpetAccess, isSignedIn: boolean): FlyingCarpetPage | null {
  if (access === "customer" && !isSignedIn) return "account/login"
  if (access === "guest" && isSignedIn) return "account"
  return null
}

function FlyingCarpetContents() {
  const carpet = useFlyingCarpet()
  const account = useAccount()
  const baseUrl = useCheckoutConfig().data?.webApiBaseUrl

  useRouterEvents((e) => {
    if (e.type === "clicked") {
      carpet.close()
    }
  })

  const page = CARPET_PAGES[carpet.page]
  const access = page?.access
  const redirect = access && account.isLoaded ? accessRedirect(access, !!account.customer) : null

  // Recheck the session whenever account pages open, since hosted checkout can also sign customers in.
  useEffect(() => {
    if (baseUrl && access) void accountStore.loadCustomer(baseUrl)
  }, [baseUrl, !!access])

  useEffect(() => {
    if (redirect) carpet.open(redirect)
  }, [redirect])

  let heading = null as ReactNode
  let contents = null as ReactNode

  if (page && carpet.page !== "none" && page.component) {
    heading = page.label
    contents =
      access && (!account.isLoaded || redirect) ? (
        <div className="h-full flex items-center justify-center">
          <Spinner className="text-[24px]" />
        </div>
      ) : (
        <page.component />
      )
  }

  return (
    <div className="flex flex-col text-blue h-full">
      <div className="flex justify-between type-label-s py-3 px-grid-margin md:px-4 items-center flex-none">
        {page?.backTarget ? (
          <button
            type="button"
            className="inline-flex items-center gap-2 outline-none"
            onClick={() => carpet.open(page.backTarget!)}
          >
            <svg className="size-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M12 5l-7 7 7 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {heading}
          </button>
        ) : (
          <span>{heading}</span>
        )}
        <Dialog.Close className="outline-none">{CLOSE_ICON}</Dialog.Close>
      </div>
      <div className="relative min-h-0 flex-1">{contents}</div>
    </div>
  )
}
